import useConnectedBAIClient from '../components/provider/BAIClientProvider/hooks/useConnectedBAIClient';
import { useSuspenseTanQuery } from '../helper/reactQueryAlias';
import { useEventNotStable } from './useEventNotStable';
import * as _ from 'lodash-es';
import { useMemo, useState } from 'react';
import { useRelayEnvironment } from 'react-relay';
import {
  commitMutation,
  GraphQLTaggedNode,
  MutationParameters,
} from 'relay-runtime';

type UseMemoizedJsonParseOptions<T> = {
  fallbackValue: T;
};

/**
 * A custom React hook that memoizes the result of parsing a JSON string.
 *
 * @template T The expected type of the parsed JSON object.
 * @param jsonString - The JSON string to parse. If `undefined` or `null`, the `fallbackValue` is returned.
 * @param options - Optional configuration object.
 * @param options.fallbackValue - The value to return if parsing fails or if `jsonString` is not a string.
 * @returns The parsed JSON object of type `T`, or the `fallbackValue` if parsing fails.
 *
 * @example
 * const data = useMemoizedJSONParse<MyType>(jsonString, { fallbackValue: defaultValue });
 */
export function useMemoizedJSONParse<T = any>(
  jsonString: string | undefined | null,
  options?: UseMemoizedJsonParseOptions<T>,
): T {
  'use memo';
  const { fallbackValue } = options || {};

  return useMemo(() => {
    if (typeof jsonString !== 'string') return fallbackValue;

    try {
      return JSON.parse(jsonString);
    } catch {
      return fallbackValue;
    }
  }, [jsonString, fallbackValue]);
}

export const useDateISOState = (initialValue?: string) => {
  'use memo';
  const [value, setValue] = useState(initialValue || new Date().toISOString());

  const update = useEventNotStable((newValue?: string) => {
    setValue(newValue || new Date().toISOString());
  });
  return [value, update] as const;
};

export const useUpdatableState = (initialValue: string) => {
  return useDateISOState(initialValue);
};

export const INITIAL_FETCH_KEY = 'first';
export const useFetchKey = () => {
  return [...useDateISOState(INITIAL_FETCH_KEY), INITIAL_FETCH_KEY] as const;
};

export const useAllowedHostNames = () => {
  'use memo';
  const baiClient = useConnectedBAIClient();
  const { data } = useSuspenseTanQuery({
    queryKey: ['useAllowedHOstNames'],
    queryFn: () => baiClient.vfolder.list_all_hosts(),
  });
  return data?.allowed;
};

export type ResourceSlotDetail = {
  slot_name: string;
  description: string;
  human_readable_name: string;
  display_unit: string;
  number_format: {
    binary: boolean;
    round_length: number;
  };
  display_icon: string;
};

export function useMutationWithPromise<T extends MutationParameters>(
  mutation: GraphQLTaggedNode,
) {
  const environment = useRelayEnvironment();
  return (variables: T['variables']) => {
    return new Promise<T['response']>((resolve, reject) => {
      commitMutation<T>(environment, {
        mutation,
        variables,
        onCompleted: (response, errors) => {
          if (errors) {
            reject(errors);
          } else {
            resolve(response);
          }
        },
        onError: (error) => {
          reject(error);
        },
      });
    });
  };
}
export {
  baiSignedRequestWithPromise,
  useBAISignedRequestWithPromise,
} from './useBAISignedRequestWithPromise';

export { default as useErrorMessageResolver } from './useErrorMessageResolver';
export { default as useViewer } from './useViewer';
export type { ErrorResponse } from './useErrorMessageResolver';
export type { ESMClientErrorResponse } from './useErrorMessageResolver';
export { default as useGetAvailableFolderName } from './useGetAvailableFolderName';
export { useInterval, useIntervalValue } from './useIntervalValue';
export {
  default as useBAILogger,
  ContextualLogger,
  LogLevel,
} from './useBAILogger';
export type { LoggerPlugin, LogContext, BAILogger } from './useBAILogger';
export { useEventNotStable } from './useEventNotStable';
// `useBAIi18n` is intentionally NOT re-exported from the package's public
// surface — it is an internal implementation detail of how BUI components
// bind to BUI's own i18next instance (see ../hooks/useBAIi18n.ts and the
// ESLint rule in eslint.config.js that forbids `useTranslation` imports
// from 'react-i18next' inside packages/backend.ai-ui/src/**). Consumers
// of the package use their own host i18n; they have no use for BUI's
// instance binding.
export {
  useProjectResourceGroups,
  StorageHostFetchError,
} from './useProjectResourceGroups';
export type { ScalingGroupItem } from './useProjectResourceGroups';
